import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ChevronUp, LayoutDashboard } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';

// Marketing Pages
import LandingPage from './pages/LandingPage';
import HowItWorksPage from './pages/HowItWorksPage';
import JourneyPage from './pages/JourneyPage';
import ForCliniciansPage from './pages/ForCliniciansPage';
import ContactPage from './pages/ContactPage';
import FaqPage from './pages/FaqPage';
import CrisisResourcesPage from './pages/CrisisResourcesPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import HipaaNoticePage from './pages/HipaaNoticePage';
import ClientPreview from './pages/ClientPreview';
import ClinicianPreview from './pages/ClinicianPreview';
import Pricing from './pages/Pricing';
import BenefitsPage from './pages/BenefitsPage';
import ResearchReferencesPage from './pages/ResearchReferencesPage';

// Application Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ClientDashboard from './pages/ClientDashboard';
import ClinicianDashboard from './pages/ClinicianDashboard';
import ClergyDashboard from './pages/ClergyDashboard';
import CoachDashboard from './pages/CoachDashboard';
import DailyContent from './pages/DailyContent';
import JournalView from './pages/JournalView';
import ProgressView from './pages/ProgressView';
import ProfileSettings from './pages/ProfileSettings';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ForgotUsername from './pages/ForgotUsername';
import ResetPassword from './pages/ResetPassword';
import AdminDashboard from './pages/AdminDashboard';
import CreateAccountPage from './pages/CreateAccountPage';
import AdminTiers from './pages/AdminTiers';
import UserDetailsPage from './pages/UserDetailsPage';
import ClientJournalView from './pages/ClientJournalView';
import FaithContentAdmin from './pages/FaithContentAdmin';
import CertificatePage from './pages/CertificatePage';
import FaithContentPage from './pages/FaithContentPage';
import SafetyPlan from './pages/SafetyPlan';
import ActivitiesPage from './pages/ActivitiesPage';
import HomeworkLibrary from './pages/HomeworkLibrary';
import ClinicianAcknowledgement from './pages/ClinicianAcknowledgement';
import CoachAcknowledgement from './pages/CoachAcknowledgement';
import ClientEnrollmentAcknowledgement from './pages/ClientEnrollmentAcknowledgement';
import ClinicianMessages from './pages/ClinicianMessages';
import ClientMessages from './pages/ClientMessages';
import ClientResourcesPage from './pages/ClientResourcesPage';
import MyAgreements from './pages/MyAgreements';
import ClientProfileView from './pages/ClientProfileView';
import HomeworkResourcesPage from './pages/HomeworkResourcesPage';
import InteractiveHomeworkPage from './pages/InteractiveHomeworkPage';
import JoinViaInvitation from './pages/JoinViaInvitation';

// Components
import LoadingSpinner from './components/LoadingSpinner';
import IdleLogoutReminder from './components/IdleLogoutReminder';
import SessionExpiredBanner from './components/SessionExpiredBanner';
import LoginInspirationPopup from './components/LoginInspirationPopup';
import SubscriptionGate from './components/SubscriptionGate';

const getDefaultPath = (user) => (user?.role === 'admin' ? '/admin' : '/dashboard');

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={getDefaultPath(user)} replace />;
  }

  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return <Navigate to={getDefaultPath(user)} replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Marketing/Public Landing Page */}
      <Route path="/" element={user ? <Navigate to={getDefaultPath(user)} replace /> : <LandingPage />} />
      
      {/* Preview Pages */}
      <Route path="/preview/client" element={<ClientPreview />} />
      <Route path="/preview/clinician" element={<ClinicianPreview />} />

      {/* Auth Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-username"
        element={
          <PublicRoute>
            <ForgotUsername />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />
      <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/clinician/acknowledge" element={
              <ProtectedRoute allowedRoles={['clinician']}>
                <ClinicianAcknowledgement />
              </ProtectedRoute>
            } />
            <Route path="/client/enrollment-acknowledgment" element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientEnrollmentAcknowledgement />
              </ProtectedRoute>
            } />
      <Route path="/clinician/messages" element={
        <ProtectedRoute allowedRoles={['clinician', 'clergy', 'coach']}>
          <ClinicianMessages />
        </ProtectedRoute>
      } />
      <Route path="/coach/acknowledge" element={
        <ProtectedRoute allowedRoles={['coach']}>
          <CoachAcknowledgement />
        </ProtectedRoute>
      } />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route path="/clinician/register" element={<Navigate to="/register" replace />} />

      {/* Dashboard Routes (Protected) */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {user?.role === 'client'
              ? <SubscriptionGate><ClientDashboard /></SubscriptionGate>
              : user?.role === 'admin'
                ? <AdminDashboard />
                : user?.role === 'clergy'
                  ? <ClergyDashboard />
                  : user?.role === 'coach'
                    ? <CoachDashboard />
                  : <ClinicianDashboard />}
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/create-account"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <CreateAccountPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/tiers"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminTiers />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users/:userId"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UserDetailsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users/:clientId/journal"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <ClientJournalView />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/faith-content"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <FaithContentAdmin />
          </ProtectedRoute>
        }
      />

      <Route
        path="/clinician/clients/:clientId/journal"
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <ClientJournalView />
          </ProtectedRoute>
        }
      />

      <Route
        path="/clinician/clients/:clientId/profile"
        element={
          <ProtectedRoute allowedRoles={['clinician', 'clergy', 'coach']}>
            <ClientProfileView />
          </ProtectedRoute>
        }
      />

      <Route path="/certificate/:certificateId" element={<CertificatePage />} />
      
      <Route
        path="/daily/:day"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <SubscriptionGate><DailyContent /></SubscriptionGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/faith"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <SubscriptionGate>
              {user ? <Navigate to={`/faith/${user.currentDay || 1}`} replace /> : <LoadingSpinner />}
            </SubscriptionGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/faith/:day"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <SubscriptionGate><FaithContentPage currentUser={user} /></SubscriptionGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/journal"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <SubscriptionGate><JournalView /></SubscriptionGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/messages"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientMessages />
          </ProtectedRoute>
        }
      />

      <Route
        path="/progress"
        element={
          <ProtectedRoute>
            <ProgressView />
          </ProtectedRoute>
        }
      />

      <Route
        path="/safety-plan"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <SubscriptionGate><SafetyPlan /></SubscriptionGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/activities"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <SubscriptionGate><ActivitiesPage /></SubscriptionGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/homework-library"
        element={
          <ProtectedRoute>
            <HomeworkLibrary />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <ProfileSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-agreements"
        element={
          <ProtectedRoute>
            <MyAgreements />
          </ProtectedRoute>
        }
      />

      <Route
        path="/client-resources"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <SubscriptionGate><ClientResourcesPage /></SubscriptionGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/homework-resources"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <SubscriptionGate><HomeworkResourcesPage /></SubscriptionGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/homework/interactive/:assignmentId"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <SubscriptionGate><InteractiveHomeworkPage /></SubscriptionGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/homework/interactive/wellness/:assignmentId"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <SubscriptionGate><InteractiveHomeworkPage /></SubscriptionGate>
          </ProtectedRoute>
        }
      />

      {/* Public Invitation Route */}
      <Route
        path="/join/:token"
        element={<JoinViaInvitation />}
      />

      {/* Marketing Pages */}
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/benefits" element={<BenefitsPage />} />
      <Route path="/journey" element={<JourneyPage />} />
      <Route path="/clinicians" element={<ForCliniciansPage />} />
      <Route path="/research-references" element={<ResearchReferencesPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/contact" element={<ContactPage />} />
        <Route path="/pricing" element={<Pricing />} />
      <Route path="/crisis-resources" element={<CrisisResourcesPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/hipaa-notice" element={<HipaaNoticePage />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const GlobalBackToTop = () => {
  const location = useLocation();
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!showBackToTop) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Back to top"
      className="fixed bottom-24 right-4 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg transition-all duration-200 hover:bg-primary-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 md:bottom-6 md:right-6"
    >
      <ChevronUp size={20} />
    </button>
  );
};

const GlobalClientDashboardShortcut = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isClient = user?.role === 'client';
  const isDashboard = location.pathname === '/dashboard';
  const isPublicRoute = [
    '/',
    '/login',
    '/register',
    '/forgot-password',
    '/forgot-username',
    '/reset-password'
  ].includes(location.pathname);

  if (!isClient || isDashboard || isPublicRoute) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => navigate('/dashboard')}
      aria-label="Return to dashboard"
      className="fixed bottom-10 right-4 z-50 inline-flex items-center gap-2 rounded-full border border-primary-200 bg-white px-3 py-2 text-xs font-semibold text-primary-700 shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:bg-primary-50 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 md:bottom-20 md:right-6"
    >
      <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
      <span>Dashboard</span>
    </button>
  );
};

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <Router>
          <AppRoutes />
          <GlobalClientDashboardShortcut />
          <GlobalBackToTop />
          <LoginInspirationPopup />
          <IdleLogoutReminder />
          <SessionExpiredBanner />
        </Router>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
